/** Default for a card whose source provides no contrary language evidence. */
export const DEFAULT_CARD_LANGUAGE = 'English' as const;

/** eBay US card-language values shared by the supported Seller Hub templates. */
export const CARD_LANGUAGES = [
  'English',
  'Arabic',
  'Basque',
  'Bengali',
  'Bulgarian',
  'Catalan',
  'Chinese',
  'Czech',
  'Danish',
  'Dutch',
  'Finnish',
  'French',
  'Galician',
  'German',
  'Greek',
  'Hebrew',
  'Hindi/Urdu',
  'Hungarian',
  'Irish',
  'Italian',
  'Japanese',
  'Korean',
  'Latin',
  'Malay',
  'Norwegian',
  'Polish',
  'Portuguese',
  'Russian',
  'Scottish Gaelic',
  'Serbian',
  'Slovak',
  'Slovenian',
  'Spanish',
  'Swedish',
  'Thai',
  'Turkish',
  'Vietnamese',
  'Welsh',
] as const;

export type CardLanguage = (typeof CARD_LANGUAGES)[number];

const LANGUAGE_MARKERS: ReadonlyArray<[CardLanguage, RegExp]> = [
  ['Japanese', /\b(?:japan|japanese)\b|[\u3040-\u30ff]/iu],
  ['Korean', /\b(?:korea|korean)\b|[\uac00-\ud7af]/iu],
  ['Chinese', /\b(?:china|chinese|mandarin|simplified|traditional)\b|[\u3400-\u9fff]/iu],
  ['French', /\b(?:france|french|francais|français)\b/iu],
  ['German', /\b(?:germany|german|deutsch)\b/iu],
  ['Italian', /\b(?:italy|italian|italiano)\b/iu],
  ['Spanish', /\b(?:spain|spanish|espanol|español)\b/iu],
  ['Portuguese', /\b(?:portugal|brazil|brazilian|portuguese)\b/iu],
  ['Dutch', /\b(?:dutch|netherlands)\b/iu],
  ['Polish', /\b(?:poland|polish)\b/iu],
  ['Russian', /\b(?:russia|russian)\b|[\u0400-\u04ff]/iu],
  ['English', /\b(?:english|united kingdom|united states|usa)\b/iu],
];

export function normalizeCardLanguage(value: string | undefined): CardLanguage | undefined {
  const normalized = value?.trim().toLocaleLowerCase('en-US') ?? '';
  return CARD_LANGUAGES.find((language) => language.toLocaleLowerCase('en-US') === normalized);
}

/** Infer only an explicitly named or script-supported language. */
export function inferCardLanguage(value: string | undefined): CardLanguage | undefined {
  const normalized = normalizeCardLanguage(value);
  if (normalized !== undefined) return normalized;
  const text = value?.trim() ?? '';
  if (text === '') return undefined;
  return LANGUAGE_MARKERS.find(([, marker]) => marker.test(text))?.[0];
}
